/**
 * Ponto único da iconografia do RESCUE-LAB.
 *
 * Regra do sistema de design: nada de emoji na interface e nada de misturar
 * estilos de ícone. Por isso todo ícone sai daqui — os genéricos embrulhados a
 * partir da biblioteca com traço e tamanho fixos, os de salvamento veicular
 * desenhados no mesmo viewBox em `dominio.tsx`. Quem usa não escolhe espessura.
 *
 * Os nomes são o que o ícone SIGNIFICA nesta aplicação, não o desenho. Trocar o
 * desenho de "reiniciar" é mexer numa linha só, e nenhum ponto de uso muda.
 *
 * Os `→` que aparecem em `src/dominio/**` são prosa didática, não ícone, e não
 * têm relação com este módulo.
 */
import {
  Activity, AlignCenterHorizontal, Ambulance, ArrowDown, ArrowUp, Ban, Bike, BrickWall, Building2, Car, CarFront, Check,
  ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Circle, ClipboardList, Copy, CornerUpLeft, DoorOpen, Download, Droplets,
  Eye, EyeOff, Flame, FlipVertical, Focus, GripHorizontal, Hammer, HardHat, HeartPulse, Info, Layers,
  ListOrdered, Lock, LockOpen, MapPin, Maximize, Minus, Moon, Move, NotebookPen, Orbit,
  Pause, PencilLine, Play, Plus, Redo2, RotateCcw, RotateCw, Route,
  Scale3d, ScanEye, ScrollText, Settings2, Shield, ShieldAlert, Siren, SlidersHorizontal,
  MousePointer2, Stethoscope, Sun, TrafficCone, Trash2, Trees, TriangleAlert, Truck, Bus, Undo2,
  Upload, Search,
  Wrench, X, Zap, ZoomIn, ZoomOut,
} from 'lucide-react'
import { comEstiloPadrao } from './base'

export { TAMANHO_ICONE, TRACO_ICONE, IconeProprio, type PropsIcone } from './base'
export * from './dominio'
export * from './vistas'

/* --- Reprodução e histórico --------------------------------------------- */
export const IconeReproduzir = comEstiloPadrao(Play, 'Reproduzir')
export const IconePausar = comEstiloPadrao(Pause, 'Pausar')
export const IconeReiniciar = comEstiloPadrao(RotateCcw, 'Reiniciar')
export const IconeRestaurar = comEstiloPadrao(RotateCcw, 'Restaurar')
export const IconeDesfazer = comEstiloPadrao(Undo2, 'Desfazer')
export const IconeRefazer = comEstiloPadrao(Redo2, 'Refazer')

/* --- Estados (nunca só cor: sempre ícone + texto) ------------------------ */
export const IconeConcluido = comEstiloPadrao(Check, 'Concluido')
export const IconeAlerta = comEstiloPadrao(TriangleAlert, 'Alerta')
export const IconeRisco = comEstiloPadrao(Siren, 'Risco')
export const IconeDerramamento = comEstiloPadrao(Droplets, 'Derramamento')
export const IconeRiscoEletrico = comEstiloPadrao(Zap, 'RiscoEletrico')
export const IconeFogo = comEstiloPadrao(Flame, 'Fogo')
export const IconeEnergia = comEstiloPadrao(Zap, 'Energia')
export const IconeQuedaLivre = comEstiloPadrao(Building2, 'QuedaLivre')
export const IconeInfo = comEstiloPadrao(Info, 'Info')
export const IconeImpedido = comEstiloPadrao(Ban, 'Impedido')
export const IconePonto = comEstiloPadrao(Circle, 'Ponto')

/* --- Ações genéricas ----------------------------------------------------- */
export const IconeAdicionar = comEstiloPadrao(Plus, 'Adicionar')
export const IconeSubtrair = comEstiloPadrao(Minus, 'Subtrair')
export const IconeRemover = comEstiloPadrao(Trash2, 'Remover')
export const IconeDuplicar = comEstiloPadrao(Copy, 'Duplicar')
export const IconeFechar = comEstiloPadrao(X, 'Fechar')
export const IconeExportar = comEstiloPadrao(Download, 'Exportar')
export const IconeImportar = comEstiloPadrao(Upload, 'Importar')
export const IconeSubir = comEstiloPadrao(ArrowUp, 'Subir')
export const IconeDescer = comEstiloPadrao(ArrowDown, 'Descer')
export const IconeVisivel = comEstiloPadrao(Eye, 'Visivel')
export const IconeOculto = comEstiloPadrao(EyeOff, 'Oculto')
export const IconeTravado = comEstiloPadrao(Lock, 'Travado')
export const IconeDestravado = comEstiloPadrao(LockOpen, 'Destravado')
export const IconeArrastar = comEstiloPadrao(GripHorizontal, 'Arrastar')
export const IconeSelecao = comEstiloPadrao(MousePointer2, 'Selecao')

/* --- Navegação da interface ---------------------------------------------- */
export const IconeChevronBaixo = comEstiloPadrao(ChevronDown, 'ChevronBaixo')
export const IconeChevronCima = comEstiloPadrao(ChevronUp, 'ChevronCima')
export const IconeChevronEsquerda = comEstiloPadrao(ChevronLeft, 'ChevronEsquerda')
export const IconeChevronDireita = comEstiloPadrao(ChevronRight, 'ChevronDireita')
export const IconeTemaClaro = comEstiloPadrao(Sun, 'TemaClaro')
export const IconeTemaEscuro = comEstiloPadrao(Moon, 'TemaEscuro')
export const IconeRegistros = comEstiloPadrao(ScrollText, 'Registros')

/* --- Câmera e manipulação do ambiente 3D --------------------------------- */
export const IconeOrbitar = comEstiloPadrao(Orbit, 'Orbitar')
export const IconeAproximar = comEstiloPadrao(ZoomIn, 'Aproximar')
export const IconeAfastar = comEstiloPadrao(ZoomOut, 'Afastar')
export const IconeEnquadrarSelecao = comEstiloPadrao(Focus, 'EnquadrarSelecao')
export const IconeRestaurarCamera = comEstiloPadrao(Maximize, 'RestaurarCamera')
export const IconeEnquadrarCena = comEstiloPadrao(ScanEye, 'EnquadrarCena')
export const IconeInteriorExtracao = comEstiloPadrao(DoorOpen, 'InteriorExtracao')
export const IconeAlinhamentoVitimaPrancha = comEstiloPadrao(AlignCenterHorizontal, 'AlinhamentoVitimaPrancha')
export const IconeVoltarEnquadramento = comEstiloPadrao(CornerUpLeft, 'VoltarEnquadramento')
/** Voltar ao módulo de onde o professor pulou para a Cinemática (ex.: a Oficina de Vítimas). */
export const IconeVoltarModulo = comEstiloPadrao(ChevronLeft, 'VoltarModulo')
export const IconeMover = comEstiloPadrao(Move, 'Mover')
export const IconeGirar = comEstiloPadrao(RotateCw, 'Girar')
export const IconeInverter = comEstiloPadrao(FlipVertical, 'Inverter')
export const IconeEscalar = comEstiloPadrao(Scale3d, 'Escalar')

/* --- Grupos de ferramentas dos módulos ----------------------------------- */
export const IconeConstrucao = comEstiloPadrao(Wrench, 'Construcao')
export const IconeCamadas = comEstiloPadrao(Layers, 'Camadas')
export const IconeCena = comEstiloPadrao(Settings2, 'Cena')
export const IconeLegenda = comEstiloPadrao(Info, 'Legenda')
export const IconeEvento = comEstiloPadrao(Zap, 'Evento')
export const IconeFisica = comEstiloPadrao(Activity, 'Fisica')
export const IconeResultados = comEstiloPadrao(ClipboardList, 'Resultados')
export const IconeMetricas = comEstiloPadrao(HeartPulse, 'Metricas')
export const IconeInvestigador = comEstiloPadrao(Search, 'Investigador')
export const IconeEquipamentos = comEstiloPadrao(SlidersHorizontal, 'Equipamentos')
export const IconeSuspensao = comEstiloPadrao(Activity, 'Suspensao')
export const IconeAvaliacao = comEstiloPadrao(ClipboardList, 'Avaliacao')
export const IconeProtecao = comEstiloPadrao(Shield, 'Protecao')
export const IconeProtecaoAlerta = comEstiloPadrao(ShieldAlert, 'ProtecaoAlerta')
export const IconeFerramenta = comEstiloPadrao(Wrench, 'Ferramenta')
export const IconeExecutar = comEstiloPadrao(Hammer, 'Executar')
export const IconePerturbar = comEstiloPadrao(Zap, 'Perturbar')
export const IconeFila = comEstiloPadrao(ListOrdered, 'Fila')
export const IconeEstadoClinico = comEstiloPadrao(HeartPulse, 'EstadoClinico')
export const IconePosicao = comEstiloPadrao(MapPin, 'Posicao')
export const IconeRota = comEstiloPadrao(Route, 'Rota')
export const IconeProcedimentos = comEstiloPadrao(ClipboardList, 'Procedimentos')
export const IconeAutoria = comEstiloPadrao(NotebookPen, 'Autoria')
export const IconeEditar = comEstiloPadrao(PencilLine, 'Editar')

/* --- Objetos do table top ------------------------------------------------ */
export const IconeCarro = comEstiloPadrao(Car, 'Carro')
export const IconeSuv = comEstiloPadrao(CarFront, 'Suv')
export const IconeCaminhao = comEstiloPadrao(Truck, 'Caminhao')
export const IconeOnibus = comEstiloPadrao(Bus, 'Onibus')
export const IconeCaminhaoBombeiros = comEstiloPadrao(Flame, 'CaminhaoBombeiros')
export const IconeBombeiro = comEstiloPadrao(HardHat, 'Bombeiro')
export const IconeBombeiroMedico = comEstiloPadrao(Stethoscope, 'BombeiroMedico')
export const IconeAreaUsa = comEstiloPadrao(Ambulance, 'AreaUsa')
export const IconeMoto = comEstiloPadrao(Bike, 'Moto')
export const IconeArvore = comEstiloPadrao(Trees, 'Arvore')
export const IconeCone = comEstiloPadrao(TrafficCone, 'Cone')
export const IconeMuro = comEstiloPadrao(BrickWall, 'Muro')
export * from './modulos'
